import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { CheckCircle2, Clock, AlertTriangle, ListTodo, ArrowUpRight } from "lucide-react";
import { Task, User, Project } from "../../types";

interface TaskListProps {
  tasks: Task[];
  projects: Project[];
  currentUser: User;
  onUpdateTaskStatus?: (taskId: string, status: Task["status"]) => void;
}

export function TaskList({
  tasks,
  projects,
  currentUser,
  onUpdateTaskStatus,
}: TaskListProps) {
  const [displayTasks, setDisplayTasks] = useState<Task[]>(tasks);

  useEffect(() => {
    setDisplayTasks(tasks);
  }, [tasks]);

  const getFilteredTasks = () => {
    if (currentUser.role === "admin") {
      return displayTasks;
    }
    if (currentUser.role === "supervisor") {
      const supervisorProjects = projects.filter(
        (project) => project.supervisorId === currentUser.id,
      );
      return displayTasks.filter((task) =>
        supervisorProjects.some((project) => project.id === task.projectId),
      );
    }
    return displayTasks.filter((task) =>
      Array.isArray(task.assignedTo)
        ? task.assignedTo.includes(currentUser.id)
        : (task.assignedTo as any) === currentUser.id,
    );
  };

  const filteredTasks = getFilteredTasks().slice(0, 6);

  const getStatusIcon = (status: Task["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "in-progress":
        return <Clock className="h-4 w-4 text-cyan-500" />;
      case "blocked":
        return <AlertTriangle className="h-4 w-4 text-rose-500" />;
      case "pending":
      default:
        return <Clock className="h-4 w-4 text-slate-400 dark:text-zinc-500" />;
    }
  };

  const getStatusBadge = (status: Task["status"]) => {
    switch (status) {
      case "completed":
        return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20";
      case "in-progress":
        return "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20";
      case "blocked":
        return "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20";
      case "pending":
      default:
        return "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20";
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "urgent":
      case "high":
        return "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20";
      case "medium":
        return "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20";
      case "low":
      default:
        return "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20";
    }
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find((entry) => entry.id === projectId);
    return project?.name || "Unassigned";
  };

  const canUpdateTask = (task: Task) => {
    const isAssigned = Array.isArray(task.assignedTo)
      ? task.assignedTo.includes(currentUser.id)
      : (task.assignedTo as any) === currentUser.id;
    return currentUser.role === "fabricator" && isAssigned;
  };

  const canMarkAsDone = (task: Task) => {
    return (
      (currentUser.role === "admin" || currentUser.role === "supervisor") &&
      task.status !== "completed"
    );
  };

  const getNextStatus = (currentStatus: Task["status"]): Task["status"] => {
    switch (currentStatus) {
      case "pending":
        return "in-progress";
      case "in-progress":
      case "blocked":
      case "completed":
      default:
        return "completed";
    }
  };

  const handleStatusUpdate = (taskId: string, status: Task["status"]) => {
    setDisplayTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId
          ? { ...task, status, updatedAt: new Date().toISOString() }
          : task,
      ),
    );
    onUpdateTaskStatus?.(taskId, status);
  };

  return (
    <Card className="overflow-hidden rounded-2xl border border-slate-200 dark:border-[#232730] bg-white dark:bg-[#121418] shadow-sm dark:shadow-xl transition-all">
      <CardHeader className="border-b border-slate-100 dark:border-[#232730] p-4 sm:p-6 pb-3.5 sm:pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9.5 w-9.5 sm:h-10 sm:w-10 items-center justify-center rounded-lg border border-slate-200 dark:border-[#232730] bg-slate-50 dark:bg-[#0E1014] text-slate-900 dark:text-white shadow-xs shrink-0">
              <ListTodo className="h-4.5 w-4.5 sm:h-5 sm:w-5 text-slate-700 dark:text-zinc-200" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base sm:text-lg font-bold tracking-tight text-slate-900 dark:text-white font-display">
                Recent Tasks
              </CardTitle>
              <p className="text-xs text-slate-500 dark:text-zinc-400 font-medium truncate">
                Monitor current assignments and execution states
              </p>
            </div>
          </div>
          <button
            onClick={() => (window.location.hash = "tasks")}
            className="text-xs font-semibold text-slate-600 dark:text-zinc-300 hover:text-slate-950 dark:hover:text-white flex items-center gap-1 transition-colors cursor-pointer shrink-0 ml-2"
          >
            View All
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 p-4 sm:p-6">
        {filteredTasks.map((task) => (
          <div
            key={task.id}
            className="rounded-xl border border-slate-200/80 dark:border-[#232730] bg-slate-50/50 dark:bg-[#0E1014]/60 p-3.5 sm:p-4 hover:border-slate-300 dark:hover:border-[#323846] transition-all"
          >
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 dark:border-[#232730] bg-white dark:bg-[#121418]">
                    {getStatusIcon(task.status)}
                  </div>
                  <div className="min-w-0">
                    <p className="break-words text-sm font-bold text-slate-900 dark:text-white">
                      {task.title}
                    </p>
                    <p className="mt-0.5 break-words text-xs text-slate-500 dark:text-zinc-400">
                      {getProjectName(task.projectId)} • Due{" "}
                      {task.dueDate
                        ? new Date(task.dueDate).toLocaleDateString()
                        : "No due date"}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${getStatusBadge(
                          task.status
                        )}`}
                      >
                        {task.status}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${getPriorityBadge(
                          task.priority
                        )}`}
                      >
                        {task.priority}
                      </span>
                    </div>
                  </div>
                </div>

                {onUpdateTaskStatus && (
                  <div className="flex flex-wrap items-center gap-2 sm:self-center">
                    {canUpdateTask(task) && task.status !== "completed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          handleStatusUpdate(task.id, getNextStatus(task.status));
                        }}
                        className="h-8 text-xs font-semibold bg-white dark:bg-[#121418] border-slate-300 dark:border-[#232730] hover:bg-slate-100 dark:hover:bg-[#1A1D24] text-slate-900 dark:text-white cursor-pointer"
                      >
                        {task.status === "pending" && "Start Task"}
                        {task.status === "in-progress" && "Complete"}
                        {task.status === "blocked" && "Unblock & Complete"}
                      </Button>
                    )}
                    {canMarkAsDone(task) && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusUpdate(task.id, "completed")}
                        className="h-8 text-xs font-bold bg-slate-950 dark:bg-white text-white dark:text-[#08090B] hover:bg-slate-800 dark:hover:bg-zinc-200 cursor-pointer"
                      >
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        Mark Done
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {filteredTasks.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 dark:border-[#232730] bg-slate-50/50 dark:bg-[#0E1014]/40 px-4 py-10 text-center text-sm text-slate-500 dark:text-zinc-400">
            No tasks available
          </div>
        )}
      </CardContent>
    </Card>
  );
}
